import { Tabs as GSTab, View } from "@gluestack-ui/themed";
import { TabsProps } from "@repo/core/components/core/Tabs/types";
import { Text } from "@repo/core/components/core/Text";
import { useDimensions } from "@repo/core/hooks/useDimensions";
import React, { forwardRef, useImperativeHandle, useState } from "react";

const getTabValue = (index: number) => `tab-${index}`;

export const Tabs = forwardRef(
  ({ tabs, initialTab = 0, children }: TabsProps, ref) => {
    const [tabSelected, setTabSelected] = useState(initialTab);
    const { isMobile } = useDimensions();
    const tabChildren = React.Children.toArray(children);

    useImperativeHandle(ref, () => {
      return {
        tabSelected
      };
    }, [tabSelected]);

    return (
      <GSTab
        flex={1}
        w="$full"
        width="$full"
        value={getTabValue(tabSelected)}
        style={{ width: "100%", alignSelf: "stretch" }}
      >
        <GSTab.TabList
          w="$full"
          alignSelf="stretch"
          justifyContent="center"
          gap={!isMobile ? "$12" : "$0"}
          style={{ width: "100%", alignSelf: "stretch" }}
        >
          {tabs?.map((item, index) => (
            <GSTab.Tab
              key={index.toString()}
              value={getTabValue(index)}
              onPress={() => setTabSelected(index)}
              flex={isMobile ? 1 : undefined}
              alignItems="center"
            >
              <Text
                color={tabSelected === index ? "$primary700" : "$secondary500"}
                fontSize="$sm"
                fontWeight={600}
              >
                {item.label}
              </Text>
              {tabSelected === index ? (
                <View
                  top="$2"
                  w="$full"
                  height="$1"
                  backgroundColor="$primary700"
                  borderTopLeftRadius={4}
                  borderTopRightRadius={4}
                />
              ) : null}
            </GSTab.Tab>
          ))}
        </GSTab.TabList>
        <View
          borderBottomColor="$borderLight200"
          borderBottomWidth={1}
          mb={!isMobile ? "$6" : "$2"}
          bg="$backgroundLight200"
          height="$px"
          w="$full"
        />
        <View
          px={!isMobile ? "$6" : "$0"}
          w="$full"
          flex={1}
          mb="$4"
          alignSelf="stretch"
          style={{ width: "100%", alignSelf: "stretch" }}
        >
          {tabChildren.map((child, index) => (
            <View
              key={getTabValue(index)}
              w="$full"
              flex={1}
              alignSelf="stretch"
              // Keep panels mounted so tab switches are instant; hide inactive tabs.
              display={tabSelected === index ? "flex" : "none"}
              pointerEvents={tabSelected === index ? "auto" : "none"}
              style={{ width: "100%", alignSelf: "stretch" }}
            >
              {child}
            </View>
          ))}
        </View>
      </GSTab>
    );
  }
);
